.overlay {
  position: fixed;
  inset-block-start: 0;
  inset-inline-start: 0;
  inline-size: 100dvw;
  block-size: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Above the global header (1000) and any sticky page chrome like the
     entity title bar (90). Without this the overlay's `z-index: auto`
     loses to fixed siblings that set an explicit stacking order. */
  z-index: 1100;
}

.overlay:not(:has(~ .overlay)) {
  background-color: oklch(from var(--color__black) l c h / 0.6);
}

.overlay:has(~ .overlay) {
  background-color: transparent;
}

.overlay:has(~ .overlay),
.overlay:not(:has(~ .overlay)):not(:is(.overlay ~ .overlay)) {
  &[data-entering] {
    animation: modal-fade 250ms;
  }

  &[data-exiting] {
    animation: modal-fade 250ms reverse;
  }
}

.modal {
  position: relative;
  overflow: auto;
  max-block-size: calc(100% - 4rem);
  inline-size: calc(100% - 4rem);
  display: flex;
  flex-direction: column;
  padding: var(--space__2xl);
  border-radius: var(--border-radius__xl);
  border: 1px solid var(--color__gray-300);
  background: var(--color__white);
  box-shadow: 0 2px 16px 0 oklch(var(--color__gray-950) / 0.25);

  overflow-block: auto;
  overscroll-behavior: contain;

  &[data-entering] {
    animation: modal-slide-in 250ms;
  }

  &[data-exiting] {
    animation: modal-slide-out 250ms;
  }
}

/*
 * React Aria focuses the dialog element itself on open, so a screen reader announces the dialog
 * by name and the first Tab reaches the first control. The element is a container, not a
 * control, so it paints no ring: the reset's `[tabindex]:focus-visible` ring would otherwise
 * outline the whole content box and cut through the close button. `:focus-visible` is part of
 * the selector because the reset's rule has the same specificity as a bare class and would win
 * on source order (see `assets/styles/reset.css`).
 */
.dialog:focus-visible {
  outline: none;
}

/*
 * The close button. Three sizes and two insets, and every one of them is derived rather
 * than picked, because what has to line up is the *glyph* and not the hit area: the box is
 * a 36px touch target with a 16px icon floating in the middle of it, so insetting the box
 * by a token puts the icon 10px away from wherever that token said.
 *
 * What the two insets aim at, both in design/50-components.md "Dialog chrome":
 *
 * - Inline: the glyph's outer edge lands on the content padding, so the x sits in the same
 *   column the title and the fields start from.
 * - Block: the glyph's centre lands on the centre of the title's ink. `Heading` trims its
 *   box to cap and baseline, so for a dialog title the ink is exactly the cap height, and a
 *   title with no descenders (most of them) is optically centred by the same number.
 *
 * The block inset is why a dialog title is `xl` and not a free choice: the modal cannot see
 * its own title, so the one number it cannot derive is the one the title size fixes. A
 * dialog that needs another title size overrides `--ds-modal-title-cap-block-size` rather
 * than nudging the inset.
 */
.close {
  --ds-modal-close-size: 2.25rem;
  --ds-modal-close-glyph-size: 1rem;
  /* Cap height of the `xl` (20px) dialog title in the primary face. Helvetica Neue's cap
     height is 0.714em, and that ratio is the one thing here that comes from the font rather
     than from a token. */
  --ds-modal-title-cap-block-size: calc(var(--font-size__xl) * 0.714);

  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--ds-modal-close-size);
  block-size: var(--ds-modal-close-size);
  border-radius: var(--border-radius__md);
  /* `gray-900`, not the `gray-600` the rest of the meta tier takes: at this size the stroke
     is a hairline, and a hairline in a mid gray reads as smudged beside a semibold title.
     This is also the control every reader reaches for first. */
  color: var(--color__gray-900);
  position: absolute;
  inset-inline-end: calc(var(--space__2xl) - (var(--ds-modal-close-size) - var(--ds-modal-close-glyph-size)) / 2);
  inset-block-start: calc(var(--space__2xl) + (var(--ds-modal-title-cap-block-size) - var(--ds-modal-close-size)) / 2);
}

/* Sized here rather than by a prop on the icon, so the hit area, the glyph and both insets
   are one block of arithmetic that cannot be half-changed. */
.close > svg {
  inline-size: var(--ds-modal-close-glyph-size);
  block-size: var(--ds-modal-close-glyph-size);
}

.close[data-hovered] {
  background-color: var(--color__gray-100);
}

@keyframes modal-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes modal-slide-in {
  from {
    opacity: 0;
    transform: translateY(-20%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes modal-slide-out {
  from {
    opacity: 1;
    transform: translateY(0);
  }

  to {
    opacity: 0;
    transform: translateY(20%);
  }
}
